/* ============================================================
   SPD Test-Instrument Hero — shell lifecycle + renderers
   One JS file powers every archetype. Adding a product =
   one new config object in spd-hero-presets.js. No deps.

   Contract (spec §5.2/§5.3):
   - Markup ships in its "passed" state; with JS disabled or
     prefers-reduced-motion the static frame simply stays.
   - Animation runs only while on-screen (IntersectionObserver,
     threshold 0.05); the frame clock resets on re-entry.
   - Fixed 70ms timestep — speed is refresh-rate independent.
   - No storage, no network; all telemetry simulated from config.

   Public API:
     SPDHero.initAll(root?)  — idempotent; auto-runs on ready
     SPDHero.instances       — live instances (debug/QA)
     instance.advance(ms)    — step the sim headlessly (QA)
   ============================================================ */
(function () {
  'use strict';

  var TICK = 70;                 /* ms per sample */
  var GRAPH_SAMPLES = 144;       /* ~10s rolling window */

  /* ---------------- utils ---------------- */

  function clone(v) {
    if (Array.isArray(v)) return v.map(clone);
    if (v && typeof v === 'object') {
      var o = {};
      for (var k in v) o[k] = clone(v[k]);
      return o;
    }
    return v;
  }
  function deepMerge(base, over) {
    if (over === null || over === undefined) return base;
    if (Array.isArray(over) || typeof over !== 'object' || typeof base !== 'object' || base === null || Array.isArray(base)) {
      return clone(over);
    }
    var out = base;
    for (var k in over) out[k] = deepMerge(base[k], over[k]);
    return out;
  }
  function smoothstep(x) { x = Math.min(1, Math.max(0, x)); return x * x * (3 - 2 * x); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }

  /* "{v.1}%" → value with 1 decimal; "{gb} GB" → % of gbOf in GB */
  function traceGb(inst, tr) {
    if (tr.key === 'vram' && inst.cfg.vramGb) return Number(inst.cfg.vramGb);
    return tr.gbOf;
  }
  function fmtNum(fmt, v, gbOf) {
    return (fmt || '{v.0}')
      .replace(/\{v\.(\d)\}/, function (_, d) { return v.toFixed(+d); })
      .replace('{gb}', ((v / 100) * (gbOf || 1)).toFixed(1).replace(/\.0$/, ''));
  }
  function hms(totalSec) {
    totalSec = Math.max(0, Math.round(totalSec));
    var h = Math.floor(totalSec / 3600),
        m = Math.floor((totalSec % 3600) / 60),
        s = totalSec % 60;
    function p(n) { return (n < 10 ? '0' : '') + n; }
    return p(h) + ':' + p(m) + ':' + p(s);
  }
  function abbrev(v) {
    if (v >= 1e9) return (v / 1e9).toFixed(1) + 'B';
    if (v >= 1e6) return (v / 1e6).toFixed(1) + 'M';
    if (v >= 1e3) return (v / 1e3).toFixed(1) + 'K';
    return String(Math.round(v));
  }

  /* ---------------- shared stat engine (map + checklist) ----------------
     ctx: { p, rampP, soakP, passIdx, passes, t } */
  function statValue(st, ctx) {
    switch (st.type) {
      case 'progress':  return fmtNum(st.fmt || '{v.1}%', ctx.p * 100);
      case 'count':     return Math.round(ctx.p * st.total) + ' / ' + st.total;
      case 'passCount': return (ctx.passIdx + 1) + ' / ' + ctx.passes;
      case 'lerp':      return fmtNum(st.fmt, st.from + (st.to - st.from) * ctx.p);
      case 'climb':     return fmtNum(st.fmt, st.from + st.rise * ctx.p + (st.wobble || 0) * Math.sin(ctx.t * 0.006));
      case 'jitter':
        st._n = (st._n || 0) * 0.8 + (Math.random() - 0.5) * st.amp * 0.9;
        return fmtNum(st.fmt, st.idle + (st.base - st.idle) * ctx.rampP + st._n);
      case 'pair':
        st._na = (st._na || 0) * 0.8 + (Math.random() - 0.5) * st.a.amp * 0.9;
        st._nb = (st._nb || 0) * 0.8 + (Math.random() - 0.5) * st.b.amp * 0.9;
        var av = st.a.idle + (st.a.base - st.a.idle) * ctx.rampP + st._na;
        var bv = st.b.idle + (st.b.base - st.b.idle) * ctx.rampP + st._nb;
        return st.fmt
          .replace(/\{a\.(\d)\}/, function (_, d) { return av.toFixed(+d); })
          .replace(/\{b\.(\d)\}/, function (_, d) { return bv.toFixed(+d); });
      case 'timer':     return hms(ctx.soakP * st.hours * 3600);
      case 'counter':   return abbrev(st.target * (ctx.overall !== undefined ? ctx.overall : ctx.p));
      case 'fixed':     return st.value;
    }
    return '';
  }
  function statDone(st, passes) {
    switch (st.type) {
      case 'progress':  return fmtNum(st.fmt || '{v.1}%', 100);
      case 'count':     return st.total + ' / ' + st.total;
      case 'passCount': return passes + ' / ' + passes;
      case 'lerp':      return fmtNum(st.fmt, st.to);
      case 'climb':     return fmtNum(st.fmt, st.from + st.rise);
      case 'jitter':    return fmtNum(st.fmt, st.base);
      case 'pair':
        return st.fmt
          .replace(/\{a\.(\d)\}/, function (_, d) { return st.a.base.toFixed(+d); })
          .replace(/\{b\.(\d)\}/, function (_, d) { return st.b.base.toFixed(+d); });
      case 'timer':     return hms(st.hours * 3600);
      case 'counter':   return abbrev(st.target);
      case 'fixed':     return st.value;
    }
    return '';
  }

  /* ---------------- shell chrome ---------------- */

  function setState(inst, idx) {
    if (inst.stateIdx === idx) return;
    inst.stateIdx = idx;
    var states = inst.cfg.states;
    var isDone = idx === states.length - 1;
    inst.els.state.textContent = states[idx];
    inst.els.state.classList.toggle('is-done', isDone);
    inst.els.verdict.textContent = isDone ? inst.cfg.verdict.pass : inst.cfg.verdict.active;
    inst.els.verdict.classList.toggle('is-done', isDone);
  }
  function setFooter(inst, vals) {
    /* the integrity row is omitted entirely when a product has no note to show */
    if (!inst.els.integrity) return;
    var s = inst.cfg.footer == null ? '' : String(inst.cfg.footer);
    if (vals) for (var k in vals) s = s.replace('{' + k + '}', vals[k]);
    inst.els.integrity.textContent = s;
  }
  function setMetaPass(inst, passNo) {
    if (inst.metaTpl.indexOf('{pass}') !== -1) {
      inst.els.meta.textContent = inst.metaTpl.replace('{pass}', passNo);
    }
  }
  function writeStatLabels(inst, stats) {
    stats.forEach(function (st, i) {
      if (inst.els.statKeys[i]) inst.els.statKeys[i].textContent = st.label;
      var v = inst.els.statVals[i];
      if (!v) return;
      v.classList.toggle('ok', !!st.ok);
      if (st.color) v.style.color = st.color;
    });
    /* The markup ships a fixed number of stat slots and the CSS grid is a fixed
       4 columns, so a preset with fewer stats would leave empty bordered cells.
       Hide the unused slots and size the grid to the stats actually present. */
    var strip = inst.section.querySelector('[data-rig-stats]');
    if (!strip) return;
    var slots = strip.children;
    for (var i = 0; i < slots.length; i++) {
      slots[i].hidden = i >= stats.length;
    }
    strip.style.gridTemplateColumns = 'repeat(' + Math.max(1, stats.length) + ', 1fr)';
  }
  function writeStats(inst, stats, ctx) {
    stats.forEach(function (st, i) {
      if (inst.els.statVals[i]) inst.els.statVals[i].textContent = statValue(st, ctx);
    });
  }
  function writeStatsDone(inst, stats, passes) {
    stats.forEach(function (st, i) {
      if (inst.els.statVals[i]) inst.els.statVals[i].textContent = statDone(st, passes);
    });
  }

  /* ============================================================
     Renderer: map — sweep (sequential fill, multi-pass) and
     bank (all cells live at a load intensity)
     ============================================================ */
  var mapRenderer = {
    mount: function (inst) {
      var m = inst.cfg.map;
      var banks = m.banks || 1;
      var rowsPer = m.rows / banks;
      var wrap = document.createElement('div');
      if (banks > 1) wrap.className = 'spd-map-banks';
      var cells = [];
      for (var b = 0; b < banks; b++) {
        if (m.bankLabels && m.bankLabels[b]) {
          var lab = document.createElement('div');
          lab.className = 'spd-map-banklabel';
          lab.textContent = m.bankLabels[b];
          wrap.appendChild(lab);
        }
        var el = document.createElement('div');
        el.className = 'spd-map'
          + (m.cellStyle === 'bay' ? ' spd-map--bays' : '')
          /* a core bank is a picture of a die, not a progress surface — it keeps
             square cells and centres, rather than stretching to fill the card */
          + (m.mode === 'bank' ? ' spd-map--bank' : '');
        el.style.setProperty('--spd-cols', m.cols);
        el.style.setProperty('--spd-rows', rowsPer);
        for (var i = 0; i < m.cols * rowsPer; i++) cells.push(el.appendChild(document.createElement('i')));
        wrap.appendChild(el);
      }
      inst.els.instrument.replaceChildren(wrap);
      inst.r = { cells: cells, total: m.cols * m.rows, painted: 0, passIdx: 0 };
      writeStatLabels(inst, m.stats);
      setMetaPass(inst, 1);
      setFooter(inst);
    },
    tick: function (inst) {
      var m = inst.cfg.map;
      if (m.mode === 'bank') return this.tickBank(inst);
      if (m.mode === 'exercise') return this.tickExercise(inst);
      var r = inst.r, passes = m.passes || 1;
      var runMs = passes * m.fillMs;
      if (inst.cycleT >= runMs + m.holdMs) {          /* loop: reset the cycle */
        inst.cycleT = 0;
        r.passIdx = 0;
        setMetaPass(inst, 1);
      }
      if (inst.cycleT >= runMs) {                      /* done + hold */
        if (inst.stateIdx !== inst.cfg.states.length - 1) this.finish(inst);
        return;
      }
      var passIdx = Math.min(passes - 1, Math.floor(inst.cycleT / m.fillMs));
      if (passIdx !== r.passIdx) {
        r.passIdx = passIdx;
        setMetaPass(inst, passIdx + 1);
      }
      var passP = (inst.cycleT % m.fillMs) / m.fillMs;
      var total = r.total;
      /* moving-inversions flavor: alternate passes walk the address
         space backwards (m.alternate) */
      var rev = m.alternate && passIdx % 2 === 1;
      /* m.verify: each pass = write sweep (amber head, dim cells) then a
         faster read-back sweep (cyan head, cells verify to green) */
      var WF = 0.55, written, verified, headCls, inVerify = false;
      if (m.verify) {
        if (passP < WF) {
          written = Math.round(passP / WF * total); verified = 0; headCls = 'head';
        } else {
          written = total; inVerify = true;
          verified = Math.round((passP - WF) / (1 - WF) * total); headCls = 'vhead';
        }
      } else {
        written = Math.round(passP * total); verified = written; headCls = 'head';
      }
      for (var i = 0; i < total; i++) {
        var cls = i < verified ? 'read' : i < written ? (m.verify ? 'wr' : 'read') : '';
        var cell = r.cells[rev ? total - 1 - i : i];
        if (cell.className !== cls) cell.className = cls;
      }
      var headIdx = inVerify ? verified - 1 : written - 1;
      var headMax = inVerify ? verified : written;
      if (headIdx >= 0 && headMax < total) {
        r.cells[rev ? total - 1 - headIdx : headIdx].className = headCls;
      }
      if (m.verify && inst.cfg.states.length > 2) {
        setState(inst, inVerify ? 1 : 0);              /* WRITE / VERIFY per pass */
      } else {
        var overall = inst.cycleT / runMs;
        setState(inst, Math.min(inst.cfg.states.length - 2, Math.floor(overall * (inst.cfg.states.length - 1))));
      }
      writeStats(inst, m.stats, { p: passP, overall: inst.cycleT / runMs, rampP: passP, soakP: 0, passIdx: passIdx, passes: passes, t: inst.t });
    },
    tickExercise: function (inst) {
      /* all bays under parallel R/W load; activity LEDs flicker and bays
         verify to steady green in randomized order */
      var m = inst.cfg.map, r = inst.r, total = r.total;
      if (inst.cycleT >= m.fillMs + m.holdMs) inst.cycleT = 0;   /* loop */
      if (inst.cycleT >= m.fillMs) {
        if (inst.stateIdx !== inst.cfg.states.length - 1) this.finish(inst);
        return;
      }
      if (!r.order) {                                  /* deterministic shuffle */
        r.order = [];
        for (var i = 0; i < total; i++) r.order.push(i);
        var seed = 48271;
        for (var j = total - 1; j > 0; j--) {
          seed = (seed * 16807) % 2147483647;
          var k = seed % (j + 1);
          var tmp = r.order[j]; r.order[j] = r.order[k]; r.order[k] = tmp;
        }
      }
      var p = inst.cycleT / m.fillMs;
      var done = Math.floor(p * total);
      for (var c = 0; c < total; c++) {
        var cell = r.cells[r.order[c]];
        var cls = c < done ? 'read' : (Math.random() < 0.45 ? 'test act' : 'test');
        if (cell.className !== cls) cell.className = cls;
      }
      setState(inst, Math.min(inst.cfg.states.length - 2, Math.floor(p * (inst.cfg.states.length - 1))));
      writeStats(inst, m.stats, { p: p, overall: p, rampP: p, soakP: 0, passIdx: 0, passes: 1, t: inst.t });
    },
    tickBank: function (inst) {
      var m = inst.cfg.map, r = inst.r, ramp = m.ramp;
      var t = inst.t;
      var ph = t < ramp.startMs ? 0 : t < ramp.endMs ? 1 : t < ramp.endMs + ramp.settleMs ? 2 : 3;
      var rampP = smoothstep((t - ramp.startMs) / (ramp.endMs - ramp.startMs));
      var load = 0.04 + 0.95 * rampP;
      if (ph === 3) {
        /* verified: cores go steady green, telemetry keeps living */
        if (inst.stateIdx !== inst.cfg.states.length - 1) {
          for (var i = 0; i < r.total; i++) r.cells[i].className = 'read';
          setState(inst, inst.cfg.states.length - 1);
        }
      } else {
        setState(inst, ph);
        for (var c = 0; c < r.total; c++) {
          /* per-core shimmer: deterministic phase/speed per cell */
          var n = 0.5 + 0.5 * Math.sin(t * (0.004 + (c % 7) * 0.0009) + c * 2.39996);
          var intensity = load * (0.62 + 0.38 * n);
          var bucket = intensity < 0.08 ? 0 : Math.min(5, 1 + Math.floor(intensity * 5));
          r.cells[c].className = bucket ? 'l' + bucket : '';
        }
      }
      writeStats(inst, m.stats, { p: rampP, rampP: rampP, soakP: 0, passIdx: 0, passes: 1, t: t });
    },
    finish: function (inst) {
      var m = inst.cfg.map, r = inst.r;
      for (var i = 0; i < r.total; i++) r.cells[i].className = 'read';
      setState(inst, inst.cfg.states.length - 1);
      writeStatsDone(inst, m.stats, m.passes || 1);
      setMetaPass(inst, m.passes || 1);
    }
  };

  /* ============================================================
     Renderer: graph — rolling multi-trace time-series
     ============================================================ */
  var graphRenderer = {
    mount: function (inst) {
      var g = inst.cfg.graph;
      var wrap = document.createElement('div');
      wrap.className = 'spd-graph';
      var canvas = document.createElement('canvas');
      wrap.appendChild(canvas);
      inst.els.instrument.replaceChildren(wrap);
      g.traces.forEach(function (tr) { tr._n = 0; tr._buf = []; });
      inst.r = { wrap: wrap, canvas: canvas, ctx: canvas.getContext('2d'), power: { n: 0 } };
      writeStatLabels(inst, g.traces.map(function (tr) {
        return { label: tr.label, color: tr.color };
      }));
      var self = this;
      inst.r.resize = function () {
        var dpr = window.devicePixelRatio || 1;
        canvas.width = wrap.clientWidth * dpr;
        canvas.height = wrap.clientHeight * dpr;
        self.draw(inst);
      };
      if (window.ResizeObserver) {
        inst.r.ro = new ResizeObserver(inst.r.resize);
        inst.r.ro.observe(wrap);
      } else {
        addEventListener('resize', inst.r.resize);
      }
      inst.r.resize();
      setFooter(inst, g.power ? { power: Math.round(g.power.idle) } : null);
    },
    baseValue: function (tr, ramp, t) {
      if (t <= ramp.startMs) return tr.idle;
      var p = smoothstep((t - ramp.startMs) / (ramp.endMs - ramp.startMs));
      return tr.idle + ((tr.soakTarget !== undefined ? tr.soakTarget : tr.base) - tr.idle) * p;
    },
    tick: function (inst) {
      var g = inst.cfg.graph, ramp = g.ramp, t = inst.t;
      var ph = t < ramp.startMs ? 0 : t < ramp.endMs ? 1 : t < ramp.endMs + ramp.settleMs ? 2 : 3;
      var self = this;
      g.traces.forEach(function (tr, i) {
        var j = ph < 2 ? tr.jitter.ramp : tr.jitter.soak;
        tr._n = tr._n * 0.8 + (Math.random() - 0.5) * j * 0.9;
        var v = clamp(self.baseValue(tr, ramp, t) + tr._n, tr.range[0], tr.range[1]);
        tr._buf.push(v);
        if (tr._buf.length > GRAPH_SAMPLES) tr._buf.shift();
        if (inst.els.statVals[i]) inst.els.statVals[i].textContent = fmtNum(tr.fmt, v, traceGb(inst, tr));
      });
      if (g.power && inst.cfg.footer.indexOf('{power}') !== -1) {
        var pw = inst.r.power;
        var pj = ph < 2 ? g.power.jitter.ramp : g.power.jitter.soak;
        pw.n = pw.n * 0.8 + (Math.random() - 0.5) * pj * 0.9;
        setFooter(inst, { power: Math.round(this.baseValue(g.power, ramp, t) + pw.n) });
      }
      setState(inst, ph);
      this.draw(inst);
    },
    draw: function (inst) {
      var r = inst.r, g = inst.cfg.graph;
      if (!r || !r.ctx) return;
      var ctx = r.ctx, w = r.canvas.width, h = r.canvas.height;
      var dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(255,255,255,.07)';
      ctx.lineWidth = 1 * dpr;
      [0.25, 0.5, 0.75].forEach(function (gl) {
        ctx.beginPath(); ctx.moveTo(0, h * gl); ctx.lineTo(w, h * gl); ctx.stroke();
      });
      var pad = 4 * dpr;
      g.traces.forEach(function (tr) {
        var buf = tr._buf, n = buf.length;
        if (n < 2) return;
        ctx.strokeStyle = tr.colorHex || tr.color;
        ctx.lineWidth = 1.6 * dpr;
        ctx.lineJoin = 'round';
        ctx.beginPath();
        for (var i = 0; i < n; i++) {
          var x = (i / (GRAPH_SAMPLES - 1)) * w;
          var norm = (buf[i] - tr.range[0]) / (tr.range[1] - tr.range[0]);
          var y = pad + (1 - norm) * (h - pad * 2);
          i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.stroke();
        var ln = (buf[n - 1] - tr.range[0]) / (tr.range[1] - tr.range[0]);
        ctx.fillStyle = tr.colorHex || tr.color;
        ctx.beginPath();
        ctx.arc(((n - 1) / (GRAPH_SAMPLES - 1)) * w, pad + (1 - ln) * (h - pad * 2), 2.5 * dpr, 0, Math.PI * 2);
        ctx.fill();
      });
    },
    finish: function (inst) {
      var g = inst.cfg.graph;
      g.traces.forEach(function (tr, i) {
        tr._buf = [];
        for (var k = 0; k < GRAPH_SAMPLES; k++) {
          tr._buf.push(tr.soakTarget + Math.sin(k * 0.55 + i) * tr.jitter.soak * 0.6);
        }
        if (inst.els.statVals[i]) inst.els.statVals[i].textContent = fmtNum(tr.fmt, tr.soakTarget, traceGb(inst, tr));
      });
      this.draw(inst);
      setState(inst, inst.cfg.states.length - 1);
      if (g.power) setFooter(inst, { power: Math.round(g.power.soakTarget) });
    }
  };

  /* ============================================================
     Renderer: checklist — subsystem board + soak timer
     ============================================================ */
  var checklistRenderer = {
    mount: function (inst) {
      var c = inst.cfg.checklist;
      var box = document.createElement('div');
      box.className = 'spd-check';
      var ul = document.createElement('ul');
      ul.className = 'spd-check-list';
      var lis = c.items.map(function (item) {
        var li = document.createElement('li');
        li.innerHTML = '<span class="mk"></span><span class="lbl"></span><span class="val"></span>';
        li.querySelector('.lbl').textContent = item.label;
        li.querySelector('.val').textContent = item.value;
        ul.appendChild(li);
        return li;
      });
      var soak = document.createElement('div');
      soak.className = 'spd-check-soak';
      soak.innerHTML = '<div class="row"><span class="k"></span><span class="v">00:00:00</span></div><div class="bar"><i></i></div>';
      soak.querySelector('.k').textContent = c.soakLabel;
      box.appendChild(ul);
      box.appendChild(soak);
      inst.els.instrument.replaceChildren(box);
      inst.r = { lis: lis, soak: soak, soakV: soak.querySelector('.v'), soakBar: soak.querySelector('.bar i') };
      writeStatLabels(inst, c.aggregate);
      setFooter(inst);
    },
    tick: function (inst) {
      var c = inst.cfg.checklist, r = inst.r;
      var itemsMs = c.items.length * c.itemMs;
      var soakStart = c.postMs + itemsMs;
      var doneAt = soakStart + c.soakMs;
      if (inst.cycleT >= doneAt + c.holdMs) {          /* loop */
        inst.cycleT = 0;
        r.lis.forEach(function (li) { li.className = ''; });
        r.soak.classList.remove('is-done');
      }
      var t = inst.cycleT;
      var ph = t < c.postMs ? 0 : t < soakStart ? 1 : t < doneAt ? 2 : 3;
      setState(inst, ph);
      /* items validate one-by-one */
      var liveIdx = ph === 0 ? -1 : Math.floor((t - c.postMs) / c.itemMs);
      r.lis.forEach(function (li, i) {
        li.className = i < liveIdx || ph >= 2 ? 'is-ok' : i === liveIdx && ph === 1 ? 'is-live' : '';
      });
      /* soak clock + bar */
      var soakP = ph < 2 ? 0 : ph === 3 ? 1 : (t - soakStart) / c.soakMs;
      r.soakV.textContent = hms(soakP * c.soakHours * 3600);
      r.soakBar.style.width = (soakP * 100).toFixed(1) + '%';
      r.soak.classList.toggle('is-done', ph === 3);
      var rampP = clamp(t / soakStart, 0, 1);
      writeStats(inst, c.aggregate, { p: rampP, rampP: rampP, soakP: soakP, passIdx: 0, passes: 1, t: inst.t });
    },
    finish: function (inst) {
      var c = inst.cfg.checklist, r = inst.r;
      r.lis.forEach(function (li) { li.className = 'is-ok'; });
      r.soakV.textContent = hms(c.soakHours * 3600);
      r.soakBar.style.width = '100%';
      r.soak.classList.add('is-done');
      setState(inst, inst.cfg.states.length - 1);
      writeStatsDone(inst, c.aggregate, 1);
    }
  };

  /* ============================================================
     Renderer: console — live bench log (RAM, concept D).
     Scripted lines stream by with running timestamps; the log
     ends in a PASS result, holds, and restarts.
     ============================================================ */
  var consoleRenderer = {
    ts: function (ms) {
      var s = ms / 1000, m = Math.floor(s / 60), r = s % 60;
      return '[' + (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r.toFixed(1) + '] ';
    },
    line: function (box, entry, visible) {
      var d = document.createElement('div');
      if (entry.cls) d.className = entry.cls;
      d.textContent = entry.text;
      box.appendChild(d);
      while (box.children.length > visible) box.removeChild(box.firstChild);
    },
    mount: function (inst) {
      var c = inst.cfg.console;
      var box = document.createElement('div');
      box.className = 'spd-console';
      inst.els.instrument.replaceChildren(box);
      /* script entries are [delayMs, cls, text]; timestamps accumulate */
      var t = 0, script = [];
      var self = this;
      c.script.forEach(function (e) {
        t += e[0];
        script.push({ t: t, cls: e[1], text: self.ts(t) + e[2] });
      });
      inst.r = { box: box, script: script, total: t, shown: 0 };
      writeStatLabels(inst, c.stats);
      setFooter(inst);
    },
    tick: function (inst) {
      var c = inst.cfg.console, r = inst.r;
      var visible = c.visible || 9, passes = c.passes || 1;
      if (inst.cycleT >= r.total + (c.holdMs || 2600)) {   /* loop */
        inst.cycleT = 0;
        r.shown = 0;
        r.box.textContent = '';
      }
      while (r.shown < r.script.length && r.script[r.shown].t <= inst.cycleT) {
        this.line(r.box, r.script[r.shown++], visible);
      }
      var p = Math.min(1, inst.cycleT / r.total);
      if (inst.cycleT >= r.total) {
        setState(inst, inst.cfg.states.length - 1);
        writeStatsDone(inst, c.stats, passes);
      } else {
        setState(inst, Math.min(inst.cfg.states.length - 2, Math.floor(p * (inst.cfg.states.length - 1))));
        writeStats(inst, c.stats, { p: p, overall: p, rampP: p, soakP: 0,
          passIdx: Math.min(passes - 1, Math.floor(p * passes)), passes: passes, t: inst.t });
      }
    },
    finish: function (inst) {
      var c = inst.cfg.console, r = inst.r;
      r.box.textContent = '';
      var self = this;
      r.script.slice(-8).forEach(function (entry) { self.line(r.box, entry, 8); });
      setState(inst, inst.cfg.states.length - 1);
      writeStatsDone(inst, c.stats, c.passes || 1);
    }
  };

  /* ============================================================
     Renderer: chassis — top-down server map (Server, concept S7).
     Validation moves through the box group by group: drive bays,
     fan wall, DIMMs, CPUs, NIC, PSUs. Parts run hot (amber,
     flickering) under test, then go green; all lit = ship it.
     Geometry is tuned to a 2-socket 12-bay 2U (R750-class).
     ============================================================ */
  var chassisRenderer = {
    NS: 'http://www.w3.org/2000/svg',
    el: function (svg, tag, attrs) {
      var e = document.createElementNS(this.NS, tag);
      for (var k in attrs) e.setAttribute(k, attrs[k]);
      svg.appendChild(e);
      return e;
    },
    mount: function (inst) {
      var c = inst.cfg.chassis, self = this;
      var box = document.createElement('div');
      box.className = 'spd-chassis-box';
      var svg = document.createElementNS(this.NS, 'svg');
      svg.setAttribute('class', 'spd-chassis');
      svg.setAttribute('viewBox', '0 0 600 248');
      box.appendChild(svg);
      inst.els.instrument.replaceChildren(box);

      this.el(svg, 'rect', { x: 8, y: 8, width: 584, height: 214, rx: 10, 'class': 'frame' });
      var groups = { storage: [], cooling: [], memory: [], compute: [], network: [], power: [] };
      var bc = (c.bays && c.bays[0]) || 3, br = (c.bays && c.bays[1]) || 4;
      for (var r = 0; r < br; r++) for (var col = 0; col < bc; col++)
        groups.storage.push(this.el(svg, 'rect', { x: 24 + col * 41, y: 24 + r * 48, width: 34, height: 40, rx: 3, 'class': 'part' }));
      for (var f = 0; f < (c.fans || 6); f++)
        groups.cooling.push(this.el(svg, 'rect', { x: 156, y: 24 + f * 31, width: 24, height: 27, rx: 3, 'class': 'part' }));
      [24, 120].forEach(function (cy) {   /* two CPU complexes: DIMMs | CPU | DIMMs */
        var per = c.dimmsPerSide || 4;
        for (var i = 0; i < per; i++) groups.memory.push(self.el(svg, 'rect', { x: 200 + i * 13, y: cy + 6, width: 9, height: 76, rx: 2, 'class': 'part' }));
        groups.compute.push(self.el(svg, 'rect', { x: 258, y: cy + 6, width: 58, height: 76, rx: 4, 'class': 'part' }));
        for (var j = 0; j < per; j++) groups.memory.push(self.el(svg, 'rect', { x: 326 + j * 13, y: cy + 6, width: 9, height: 76, rx: 2, 'class': 'part' }));
      });
      groups.power.push(this.el(svg, 'rect', { x: 470, y: 24, width: 106, height: 52, rx: 4, 'class': 'part' }));
      groups.power.push(this.el(svg, 'rect', { x: 470, y: 84, width: 106, height: 52, rx: 4, 'class': 'part' }));
      groups.network.push(this.el(svg, 'rect', { x: 470, y: 144, width: 106, height: 26, rx: 3, 'class': 'part' }));
      var bmc = this.el(svg, 'rect', { x: 470, y: 178, width: 106, height: 28, rx: 3, 'class': 'part' });
      (c.labels || []).forEach(function (l, i) {
        var tx = self.el(svg, 'text', { x: [80, 168, 295, 523][i], y: 240, 'text-anchor': 'middle' });
        tx.textContent = l;
      });

      /* validation schedule: BMC first, then group by group (S7 timings) */
      var sched = [{ p: bmc, t0: 150, dur: 550 }];
      groups.storage.forEach(function (p, i) { sched.push({ p: p, t0: 800 + i * 95, dur: 620 }); });
      groups.cooling.forEach(function (p, i) { sched.push({ p: p, t0: 2150 + i * 95, dur: 520 }); });
      groups.memory.forEach(function (p, i) { sched.push({ p: p, t0: 3050 + i * 70, dur: 520 }); });
      groups.compute.forEach(function (p, i) { sched.push({ p: p, t0: 4400 + i * 220, dur: 900 }); });
      groups.network.forEach(function (p) { sched.push({ p: p, t0: 5650, dur: 520 }); });
      groups.power.forEach(function (p, i) { sched.push({ p: p, t0: 5950 + i * 220, dur: 620 }); });
      var order = ['storage', 'cooling', 'memory', 'compute', 'network', 'power'];
      var gEnd = {};
      order.forEach(function (g) {
        gEnd[g] = 0;
        groups[g].forEach(function (p) {
          sched.forEach(function (s) { if (s.p === p) gEnd[g] = Math.max(gEnd[g], s.t0 + s.dur); });
        });
      });
      inst.r = { sched: sched, order: order, gEnd: gEnd, total: 7000,
        stateAt: [[0, 0], [800, 1], [2150, 2], [3050, 3], [4400, 4], [5650, 5], [5950, 6]] };
      writeStatLabels(inst, c.stats);
      setFooter(inst);
    },
    paint: function (inst, t) {
      inst.r.sched.forEach(function (s) {
        var cls = 'part';
        if (t >= s.t0 + s.dur) cls = 'part ok';
        else if (t >= s.t0) {
          cls = 'part hot';
          s.p.setAttribute('fill-opacity', (0.7 + Math.random() * 0.3).toFixed(2));
        }
        if (s.p.getAttribute('class') !== cls) {
          s.p.setAttribute('class', cls);
          if (cls !== 'part hot') s.p.removeAttribute('fill-opacity');
        }
      });
    },
    tick: function (inst) {
      var c = inst.cfg.chassis, r = inst.r;
      if (inst.cycleT >= r.total + (c.holdMs || 2600)) inst.cycleT = 0;   /* loop */
      var t = inst.cycleT;
      if (t >= r.total) {
        if (inst.stateIdx !== inst.cfg.states.length - 1) this.finish(inst);
        return;
      }
      this.paint(inst, t);
      var done = 0;
      r.order.forEach(function (g) { if (r.gEnd[g] <= t) done++; });
      var idx = 0;
      r.stateAt.forEach(function (s) { if (t >= s[0]) idx = s[1]; });
      setState(inst, Math.min(idx, inst.cfg.states.length - 2));
      writeStats(inst, c.stats, { p: done / r.order.length, overall: t / r.total,
        rampP: clamp(t / r.total, 0, 1), soakP: 0, passIdx: 0, passes: 1, t: inst.t });
    },
    finish: function (inst) {
      this.paint(inst, inst.r.total + 1);
      setState(inst, inst.cfg.states.length - 1);
      writeStatsDone(inst, inst.cfg.chassis.stats, 1);
    }
  };

  var renderers = { map: mapRenderer, graph: graphRenderer, checklist: checklistRenderer, console: consoleRenderer, chassis: chassisRenderer };

  /* ============================================================
     Instance lifecycle
     ============================================================ */

  function stepOnce(inst) {
    inst.t += TICK;
    inst.cycleT += TICK;
    inst.renderer.tick(inst);
  }

  function makeInstance(section, cfg, archetype) {
    var els = {
      rig: section.querySelector('.spd-rig'),
      label: section.querySelector('[data-rig-label]'),
      state: section.querySelector('[data-rig-state]'),
      model: section.querySelector('[data-rig-model]'),
      meta: section.querySelector('[data-rig-meta]'),
      instrument: section.querySelector('[data-rig-instrument]'),
      statKeys: Array.prototype.slice.call(section.querySelectorAll('[data-rig-stats] .k')),
      statVals: Array.prototype.slice.call(section.querySelectorAll('[data-rig-stats] .v')),
      integrity: section.querySelector('[data-rig-integrity]'),
      verdict: section.querySelector('[data-rig-verdict]')
    };
    var inst = {
      section: section, cfg: cfg, archetype: archetype,
      els: els, renderer: renderers[archetype],
      t: 0, cycleT: 0, acc: 0, lastT: 0, raf: 0,
      running: false, active: false, stateIdx: -1,
      metaTpl: cfg.meta || ''
    };

    function frame(now) {
      if (!inst.running) return;
      inst.acc += now - inst.lastT;
      inst.lastT = now;
      if (inst.acc > TICK * 4) inst.acc = TICK;   /* tab-switch guard: no burst catch-up */
      while (inst.acc >= TICK) { inst.acc -= TICK; stepOnce(inst); }
      inst.raf = requestAnimationFrame(frame);
    }
    inst.start = function () {
      if (inst.running || !inst.active) return;
      inst.running = true;
      inst.acc = 0;
      inst.lastT = performance.now();            /* reset frame clock — no dt spike on re-entry */
      inst.raf = requestAnimationFrame(frame);
    };
    inst.stop = function () {
      inst.running = false;
      cancelAnimationFrame(inst.raf);
    };
    inst.advance = function (ms) {               /* headless QA hook */
      var n = Math.floor(ms / TICK);
      for (var i = 0; i < n; i++) stepOnce(inst);
    };
    inst.finish = function () {
      inst.stop();
      if (inst.active) inst.renderer.finish(inst);
    };
    return inst;
  }

  function activate(inst) {
    if (inst.active) return;
    inst.active = true;
    /* the head label is per-class copy ("FULL-SPAN SURFACE READ" vs
       "BENCH 05 · LIVE LOG"), so it comes from the preset like model/meta —
       otherwise the markup's own fallback leaks one class's label onto all. */
    if (inst.cfg.label && inst.els.label) inst.els.label.textContent = inst.cfg.label;
    if (inst.cfg.model && inst.els.model) inst.els.model.textContent = inst.cfg.model;
    if (inst.metaTpl && inst.els.meta && inst.metaTpl.indexOf('{pass}') === -1) {
      inst.els.meta.textContent = inst.metaTpl;
    }
    inst.renderer.mount(inst);
    inst.io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.isIntersecting ? inst.start() : inst.stop(); });
    }, { threshold: 0.05 });
    inst.io.observe(inst.els.rig);
  }

  function initSection(section) {
    if (section.getAttribute('data-spd-hero-ready')) return null;
    var cfgEl = section.querySelector('script[data-spd-hero-config]');
    if (!cfgEl) return null;
    var raw;
    try { raw = JSON.parse(cfgEl.textContent); } catch (e) { return null; }
    var presets = window.SPDHeroPresets || {};
    var cfg = clone(presets[raw.preset] || {});
    cfg = deepMerge(cfg, raw.overrides || {});
    /* section "custom telemetry" JSON merges into the preset root,
       e.g. {"graph":{"traces":[...]}} or {"map":{"fillMs":9000}} */
    if (raw.overrides && raw.overrides.telemetry) cfg = deepMerge(cfg, raw.overrides.telemetry);
    var archetype = (raw.overrides && raw.overrides.archetype) || cfg.archetype;
    if (!renderers[archetype] || !cfg[archetype]) return null;
    section.setAttribute('data-spd-hero-ready', '1');

    var inst = makeInstance(section, cfg, archetype);
    /* SPD_HERO_FORCE_REDUCED: dev-harness QA hook (?rm=1) */
    var motion = window.SPD_HERO_FORCE_REDUCED ? { matches: true } : matchMedia('(prefers-reduced-motion: reduce)');
    if (!motion.matches) activate(inst);
    if (motion.addEventListener) {
      motion.addEventListener('change', function () {
        if (motion.matches) {
          inst.finish();                         /* one static "passed" frame, no loop */
        } else {
          inst.active ? inst.start() : activate(inst);
        }
      });
    }
    SPDHero.instances.push(inst);
    return inst;
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-spd-hero]');
    for (var i = 0; i < nodes.length; i++) initSection(nodes[i]);
  }

  var SPDHero = { initAll: initAll, instances: [], TICK: TICK };
  window.SPDHero = SPDHero;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
  /* Shopify theme editor: sections re-render in place */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    SPDHero.instances = SPDHero.instances.filter(function (inst) {
      if (e.target && e.target.contains(inst.section)) { inst.stop(); return false; }
      return true;
    });
  });
})();

